/**
 * Typed, validated access to Vite env vars. Throws at startup if required
 * values are missing so we fail fast instead of hitting cryptic errors later.
 */

function required(key: keyof ImportMetaEnv): string {
  const v = import.meta.env[key];
  if (!v || typeof v !== 'string') {
    throw new Error(`Missing required env var: ${String(key)}`);
  }
  return v;
}

function optional(key: keyof ImportMetaEnv): string | undefined {
  const v = import.meta.env[key];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const env = {
  apiUrl: required('VITE_API_URL'),
  auth0: {
    domain: required('VITE_AUTH0_DOMAIN'),
    clientId: required('VITE_AUTH0_CLIENT_ID'),
    audience: required('VITE_AUTH0_AUDIENCE'),
    // Auth0 connection names differ per tenant. Staging (student tenant) uses
    // the Auth0 default 'Username-Password-Authentication'; prod (dev-nyayassist
    // tenant) names its database connection 'NyayAssist'. Requesting a name not
    // enabled on the client → "the connection is not enabled". Configurable so
    // one build works against either tenant.
    dbConnection: optional('VITE_AUTH0_DB_CONNECTION') ?? 'Username-Password-Authentication',
    googleConnection: optional('VITE_AUTH0_GOOGLE_CONNECTION') ?? 'google-oauth2',
  },
  datadog: {
    applicationId: optional('VITE_DATADOG_APPLICATION_ID'),
    clientToken: optional('VITE_DATADOG_CLIENT_TOKEN'),
  },
  /** origin of the add-in itself — used for Auth0 redirect_uri */
  origin: window.location.origin,
};

/// <reference types="vite/client" />
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_URL: string;
    readonly VITE_AUTH0_DOMAIN: string;
    readonly VITE_AUTH0_CLIENT_ID: string;
    readonly VITE_AUTH0_AUDIENCE: string;
    readonly VITE_AUTH0_DB_CONNECTION?: string;
    readonly VITE_AUTH0_GOOGLE_CONNECTION?: string;
    readonly VITE_DATADOG_APPLICATION_ID?: string;
    readonly VITE_DATADOG_CLIENT_TOKEN?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}
